// Blog articles. Rendered by src/pages/Blog.jsx and pre-rendered for crawlers
// by the statbox-sitemap plugin (via seoPages.mjs), so both read this file.
// Body blocks: a string is a paragraph, { list: [...] } is a bullet list,
// { steps: [...] } is a numbered list.

const APP_LINKS = [
  { href: '/app-download', label: 'Download the Statbox app' },
  { href: '/book-demo', label: 'Book a 30-minute demo' },
]

export const ARTICLES = [
  {
    slug: 'ai-football-stats-and-highlights-app',
    published: '2026-10-21',
    readMinutes: 5,
    kicker: 'Football',
    title: 'AI Football Stats and Highlights App: How Statbox Works | Statbox',
    h1: 'The AI football stats and highlights app for the games you actually play',
    description:
      'How Statbox uses AI video analysis to turn a recording of your own football match into player stats, a highlight clip for every player, and a full replay.',
    intro:
      'Professional players get a stats sheet and a highlight reel after every match. Everyone else gets a group chat and a memory of the goal they think they scored. Statbox is an AI football stats and highlights app that closes that gap for 5-a-side, 7-a-side, and 11-a-side games.',
    sections: [
      {
        h2: 'What you get after the match',
        body: [
          'Every player in the game gets their own line of stats, a highlight clip of their moments, and access to the full replay in the Statbox app. The football stats Statbox tracks are:',
          {
            list: [
              'Goals and assists',
              'Shots on target and shots off target',
              'Chances created',
              'Dribbles',
              'Tackles',
              'Saves',
            ],
          },
          'Each event is pinned to its moment in the video. Tap a goal or a tackle and the replay jumps to a few seconds before it, so you can watch exactly what happened instead of arguing about it.',
          'Players also get a match performance rating with one thing to work on next time, and the stats add up across games on their profile and on community leaderboards.',
        ],
      },
      {
        h2: 'How the AI turns video into stats',
        body: [
          'Statbox starts from a recording of the full match. AI video analysis follows the players and the ball, detects events like shots, goals, and tackles, and assigns each one to a player. The same pass cuts the highlight clips, so a player’s reel is built from the moments in their stats line.',
          'Fully automatic stats are fast, but they make mistakes that matter to players: a goal credited to the wrong teammate, a deflection counted as a shot. So a Statbox analyst reviews every game before the report goes out. Automated analysis does the heavy lifting, and a person checks the result. You are emailed when the report is ready.',
        ],
      },
      {
        h2: 'Two ways to record the game',
        body: [
          {
            list: [
              'Play at a Statbox venue. Some pitches already have Statbox cameras installed, so the match is recorded with the booking and there is nothing to set up.',
              'Film it yourself. Put a phone or camera on a stable mount where it can see the whole pitch, record the full game, and upload the MP4.',
            ],
          },
          'You do not need to buy special equipment to start. If you are filming yourself, our guide to filming a football match for AI analysis covers camera height, angle, and the mistakes that cost you stats.',
        ],
      },
      {
        h2: 'Who uses it',
        body: [
          'Players use the Statbox app on iOS and Android to join games, open their stats, and share their highlights. Community organisers use it to fill their weekly game and give people a reason to come back. Academies and coaches use the match timeline to review games with players, and venues use Statbox cameras to offer replays and stats with every booking.',
        ],
      },
      {
        h2: 'Getting started',
        body: [
          'If you play in a group that already uses Statbox, download the app and join the game. If you run a community, academy, or venue, book a 30-minute demo and we will walk through a real match, the setup, and the cost.',
        ],
      },
    ],
    related: [
      { href: '/sports/football', label: 'Statbox for football' },
      { href: '/blog/how-to-film-football-match-for-ai-analysis', label: 'How to film a match for AI stats' },
      ...APP_LINKS,
    ],
  },
  {
    slug: 'how-to-film-football-match-for-ai-analysis',
    published: '2026-10-21',
    readMinutes: 4,
    kicker: 'Guide',
    title: 'How to Film a Football Match on Your Phone for AI Stats | Statbox',
    h1: 'How to film a football match on your phone for AI stats and highlights',
    description:
      'Camera position, height, framing, and settings for filming a 5-a-side or 11-a-side football match so AI analysis can produce accurate stats and highlights.',
    intro:
      'AI match analysis can only count what the camera sees. A goal that happens out of frame is a goal nobody gets credit for. The good news is that a phone is enough, as long as you set it up properly before kick-off.',
    sections: [
      {
        h2: 'The short version',
        body: [
          {
            steps: [
              'Film in landscape, never portrait.',
              'Mount the phone. Do not hold it.',
              'Get it high, near the halfway line.',
              'Keep both goals and all four corners in frame.',
              'Record the whole match in one take if you can.',
              'Check your battery and storage before kick-off.',
            ],
          },
        ],
      },
      {
        h2: 'Position: high and central',
        body: [
          'The best spot is level with the halfway line, as high as you can safely get it. Height matters more than distance: from above, players overlap less, the ball is easier to follow, and the far side of the pitch is not squashed into a thin strip.',
          'For 5-a-side and other small-sided pitches, a tripod or clamp on the fence or a raised stand near halfway usually works. For a full-size pitch, look for a stand, a balcony, or a telescopic pole mount.',
        ],
      },
      {
        h2: 'Framing: the whole pitch, all the time',
        body: [
          'Zoom out until both goals and all four corners are in shot, with a little margin. It will feel too wide. That is fine: the analysis needs the whole pitch more than it needs close-ups.',
          'Do not pan or zoom during the match. A fixed camera keeps every player in view and gives the AI a stable picture to track from. Leave the camera still.',
        ],
      },
      {
        h2: 'Settings that help',
        body: [
          {
            list: [
              'Resolution: 1080p is plenty. 4K produces huge files that take longer to upload without much gain.',
              'Frame rate: 30 or 60 fps both work. Higher is better for fast moments like shots.',
              'Turn on airplane mode or do-not-disturb so a call does not stop the recording.',
              'If you play under floodlights, check the picture before kick-off. Avoid pointing the camera straight into a light.',
            ],
          },
        ],
      },
      {
        h2: 'Common mistakes that cost you stats',
        body: [
          {
            list: [
              'Filming from behind one goal. The far end of the pitch becomes unreadable.',
              'Portrait video. Half the pitch is cut off.',
              'Holding the phone. Your arms get tired, the picture drifts, and moments are missed.',
              'Stopping and starting. Gaps in the recording are gaps in the stats.',
              'A fence or net right in front of the lens. Move the camera so the mesh is not in focus.',
            ],
          },
        ],
      },
      {
        h2: 'After the match',
        body: [
          'Upload the full MP4 to Statbox. AI video analysis drafts the stats and highlights, a Statbox analyst reviews the game, and every player gets their stats and their clip in the app. If your venue already has Statbox cameras, you can skip all of this and just play.',
        ],
      },
    ],
    related: [
      { href: '/blog/ai-football-stats-and-highlights-app', label: 'How Statbox AI football stats work' },
      { href: '/sports/football', label: 'Statbox for football' },
      ...APP_LINKS,
    ],
  },
  {
    slug: '5-a-side-football-stats-app',
    published: '2026-10-21',
    readMinutes: 4,
    kicker: 'Communities',
    title: '5-a-Side Football Stats App: Goals, Assists and Highlights | Statbox',
    h1: 'A 5-a-side football stats app that makes people turn up next week',
    description:
      'Track goals, assists, and saves for your weekly 5-a-side game, give every player a highlight clip, and keep a leaderboard that brings the group back every week.',
    intro:
      'Every weekly 5-a-side group has the same problem: someone drops out on the day, the game is short, and the organiser spends the afternoon chasing replacements. Stats will not fix that on their own, but they give people a reason to care about next week.',
    sections: [
      {
        h2: 'Why stats change a casual game',
        body: [
          'When the game is recorded and everyone gets their own stats and highlight, a kickabout becomes something with a record. Players check their goals and assists, compare themselves on the leaderboard, and send their clip to friends. That is the part that gets people asking when the next game is.',
        ],
      },
      {
        h2: 'What a 5-a-side stats app should track',
        body: [
          'Small-sided football is fast and crowded, so the stats need to reflect what actually decides those games. Statbox tracks, for every player:',
          {
            list: [
              'Goals and assists',
              'Shots on and off target',
              'Chances created',
              'Dribbles and tackles',
              'Saves, for whoever is in goal',
            ],
          },
          'Every event links to its moment in the replay, and each player gets a highlight clip built from their own moments.',
        ],
      },
      {
        h2: 'Running the game from one place',
        body: [
          'Statbox is also where the game is organised. As an organiser you put the match on the calendar with the day, time, and place, players join from the app, and you name who played so each report belongs to a person rather than a team total. If you charge for the game, you can set a price per player when you create it.',
          'After the match, players open the app to find the replay, their stats line, and their highlight. Over the season, those stats build up on their profile and on your community leaderboard.',
        ],
      },
      {
        h2: 'Recording the game',
        body: [
          'If your pitch has Statbox cameras, the game is recorded automatically. If not, film the match on a phone mounted high near halfway with the whole pitch in frame, and upload the MP4. AI video analysis drafts the stats and highlights, and a Statbox analyst checks the game before the report goes out.',
        ],
      },
      {
        h2: 'How to start',
        body: [
          'Most groups play their first Statbox game within a week. Book a 30-minute demo and we will show you a real game, how the setup works for your pitch, and what it costs.',
        ],
      },
    ],
    related: [
      { href: '/solutions/communities', label: 'Statbox for community organisers' },
      { href: '/blog/how-to-film-football-match-for-ai-analysis', label: 'How to film your game' },
      ...APP_LINKS,
    ],
  },
  {
    slug: 'ai-padel-match-analysis',
    published: '2026-10-21',
    readMinutes: 4,
    kicker: 'Padel',
    title: 'AI Padel Match Analysis: Stats and Highlights From Every Point | Statbox',
    h1: 'AI padel match analysis: see who won the point, and how',
    description:
      'How AI padel match analysis turns a court recording into winners, errors, smashes, and rallies for every player, plus the match video and highlights.',
    intro:
      'Padel points end fast and memories are generous. AI padel match analysis replaces “I think we lost on errors” with the actual count, and gives every player the clips to prove their best shots.',
    sections: [
      {
        h2: 'The padel stats Statbox tracks',
        body: [
          'Statbox breaks the match into points and tracks, for each player:',
          {
            list: [
              'Winners',
              'Aces',
              'Smashes',
              'Lobs',
              'Drop shots',
              'Long rallies',
              'Double faults',
              'Unforced errors',
            ],
          },
          'Winners against unforced errors is usually the most useful number in a recreational match. It shows whether you are losing points to your opponents or to yourself.',
        ],
      },
      {
        h2: 'Highlights that are actually worth sending',
        body: [
          'A generic clip of the whole match is not something anyone watches twice. Statbox cuts highlights from the moments in the stats: the winners, the smashes, and the long rallies. You can share yours before you have left the club.',
        ],
      },
      {
        h2: 'How the analysis works',
        body: [
          'AI video analysis follows the ball and the four players through the recording, splits the match into points, and classifies how each point ended. A Statbox analyst then reviews the match before the report is delivered, so the numbers players see have been checked by a person.',
        ],
      },
      {
        h2: 'Recording a padel match',
        body: [
          'Many clubs install Statbox cameras on their courts, so the recording comes with the booking. If your club does not have them, you can film the match yourself: mount the camera behind the back glass, high and centred, with the full court and both sides of the net in view, and upload the MP4.',
        ],
      },
      {
        h2: 'For padel clubs',
        body: [
          'For venues, Statbox cameras turn every booking into a replay players can watch back, with stats and highlights as a paid extra. We install and calibrate the cameras and handle the analysis, so your staff are not filming or editing anything.',
        ],
      },
    ],
    related: [
      { href: '/sports/padel', label: 'Statbox for padel' },
      { href: '/solutions/venues', label: 'Statbox for clubs and venues' },
      ...APP_LINKS,
    ],
  },
  {
    slug: 'ai-basketball-stats-from-video',
    published: '2026-10-21',
    readMinutes: 4,
    kicker: 'Basketball',
    title: 'AI Basketball Stats From Video: Box Score and Highlights | Statbox',
    h1: 'AI basketball stats from video, without a stat keeper',
    description:
      'Get a full basketball box score and highlight clips from a recording of your game. Points, threes, rebounds, assists, blocks, and turnovers for every player.',
    intro:
      'Keeping a basketball box score by hand needs at least one person who is not playing, and they still miss things. AI basketball stats from video do it from one recording of the game, and every number comes with the clip that proves it.',
    sections: [
      {
        h2: 'The box score Statbox builds',
        body: [
          'From a recording of the game, Statbox tracks for each player:',
          {
            list: [
              'Points',
              'Field goals',
              'Three-pointers',
              'Free throws',
              'Rebounds',
              'Assists',
              'Blocks',
              'Turnovers',
            ],
          },
          'Players see their line in the Statbox app next to the replay, and their highlights are cut from the same moments.',
        ],
      },
      {
        h2: 'How video becomes a box score',
        body: [
          'AI video analysis tracks the players and the ball across the full game, detects shots, makes, rebounds, and passes, and attributes each one to a player. Because a wrong attribution ruins a box score, a Statbox analyst reviews the game before anyone sees the numbers.',
        ],
      },
      {
        h2: 'Filming a basketball game',
        body: [
          {
            list: [
              'Mount the camera high on the sideline, near half court.',
              'Keep the whole court, including both baskets, in frame.',
              'Leave the camera still. Do not follow the ball.',
              'Record the full game and upload the MP4.',
            ],
          },
          'If you play on a court with Statbox cameras, the game is recorded for you.',
        ],
      },
      {
        h2: 'For coaches and academies',
        body: [
          'Coaches use the match timeline to filter by player and event, then watch the clip together. Instead of describing a defensive lapse from memory, you press play on it. The next week, the same player’s history is still on their page.',
        ],
      },
    ],
    related: [
      { href: '/sports/basketball', label: 'Statbox for basketball' },
      { href: '/solutions/academies', label: 'Statbox for academies and coaches' },
      ...APP_LINKS,
    ],
  },
]

export function getArticle(slug) {
  return ARTICLES.find((article) => article.slug === slug) || null
}
